import type { CSSProperties } from 'react'
import type { AppStatus } from '../../models/careOSPrototype'
import { APP_STATUS_META } from '../../models/careOSPrototype'
import {
  BaselineCompleteIcon,
  NeverLoggedInIcon,
  OnboardingIcon,
  StatusIndicatorPill,
} from 'components/ui'

// Status indicator shown inside the Gut Checks card's detail row. Tells a
// provider where the patient is in getting started with the app:
// never-logged-in → onboarding → baseline-complete (the persistent check
// mark). never-logged-in / onboarding render as an icon+label pill;
// baseline-complete renders icon-only (see COMPACT_ONLY below). A
// tooltip/aria always carries the full meaning regardless of which form
// renders.

// Per-state visual tokens, shared with the roster's Care Coordination column
// (`HealthCoachDashboard/CareCoordinationPill.tsx`) via `StatusIndicatorPill`
// — see that file for the statuses it has no equivalent for. `fg` drives icon
// + label; `tint` is the pill fill. Colors reference the careOS palette
// (careos-prototype.css) where a legible var exists; onboarding uses a
// darkened amber because the palette's --orange (#feb117) is too light for
// text/icon on a light tint.
const STYLES: Record<AppStatus, { fg: string; tint: string; icon: JSX.Element }> = {
  'never-logged-in': {
    fg: 'var(--blue-grey)',
    tint: 'rgba(113, 143, 154, 0.12)',
    icon: <NeverLoggedInIcon />,
  },
  onboarding: {
    fg: '#B07400',
    tint: 'rgba(254, 177, 23, 0.18)',
    icon: <OnboardingIcon />,
  },
  'baseline-complete': {
    fg: 'var(--grass)',
    tint: 'rgba(19, 190, 147, 0.16)',
    icon: <BaselineCompleteIcon size={14} />,
  },
}

interface Props {
  status: AppStatus
  style?: CSSProperties
}

// Baseline-complete is permanent — once earned it never regresses, so
// spelling it out in text next to the (much more useful) Last Survey date is
// redundant. Render it as just the checkmark-in-circle, letting the icon
// alone signal "this account is active." The earlier two states still get
// the full icon+label pill since there's no other cue on the card yet.
const COMPACT_ONLY: AppStatus[] = ['baseline-complete']

export function GutCheckAppStatus({ status, style }: Props) {
  const { fg, tint, icon } = STYLES[status]
  const { label, description } = APP_STATUS_META[status]

  return (
    <StatusIndicatorPill
      label={label}
      description={description}
      fg={fg}
      tint={tint}
      icon={icon}
      compact={COMPACT_ONLY.includes(status)}
      style={style}
    />
  )
}

export default GutCheckAppStatus
